import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, FormRow, Hint, Section } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { CreateLocationMutationVariables } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import AutoCompleteSearchField from 'modules/core/components/AutoCompleteSearchField'
import { GoogleMapsContext } from 'modules/core/components/GoogleMaps/GoogleMapsProvider'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import countryList from 'modules/core/lib/countryList'
import { handleResult } from 'modules/core/lib/handleMutation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import cache from 'modules/core/lib/mobx-rest-cache'
import { timezoneOptions } from 'modules/core/lib/timezones'

import attendancePolicies from 'modules/attendance/store/collections/policies'
import AttendanceSettings from 'modules/attendance/store/collections/settings'
import EditLocationButton from 'modules/locations/components/GoogleMaps/Buttons/EditLocationButton'
import MapSidePanel from 'modules/locations/components/GoogleMaps/MapSidePanel'
import StaticMap from 'modules/locations/components/GoogleMaps/StaticMap'
import { useAutoCompleteAddress } from 'modules/locations/hooks/useAutocompleteAddress'
import { useCreateLocation } from 'modules/locations/hooks/useCreateLocation'

interface WorkplaceFormValues {
  name: string
  phoneNumber?: string
  addressLineOne: string
  addressLineTwo?: string
  country: string
  timezone: string
  coordinates: string
  postalCode: string
  state: string
  city: string
  latitude?: number
  longitude?: number
  radius: number
  siret?: string
}

const LocationWorkplacesForm = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { company } = useCurrentSession()
  const { createLocation } = useCreateLocation()
  const [showConfirmAddressPopover, setShowConfirmAddressPopover] = React.useState<boolean>(false)

  const { featureAvailability: hasGeofencingFeatureAvailability } = useFeatureAvailability(
    Features.time_tracking_geofencing_alerts
  )
  const hasGeofencingFeature = hasGeofencingFeatureAvailability.available
  const hasGeoBlockingFeature = hasFeature('dev_time_tracking_geofencing_block')
  const showGelocation = hasGeofencingFeature || hasGeoBlockingFeature

  useAsyncData(() => {
    if (!showGelocation) return Promise.resolve()

    return Promise.all([AttendanceSettings.fetch(), attendancePolicies.fetch()])
  }, [])

  const policiesId = AttendanceSettings.policyIdWithGeolocation()

  const hasGeoLocation = showGelocation && attendancePolicies.hasEmployees(policiesId)

  const inputRef = useLazyRef<HTMLInputElement | null>(() => null)
  const [showMapPanel, setShowMapPanel] = React.useState<boolean>(false)

  const handleCreate = async (variables: CreateLocationMutationVariables) => {
    const result = await createLocation(variables)
    const handled = handleResult(result, (errors) => {
      workplaceForm.setErrors({ ...errors })
    })
    return handled
  }

  const workplaceForm = useForm<WorkplaceFormValues>({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    defaultValues: {
      name: '',
      phoneNumber: '',
      addressLineOne: '',
      country: '',
      timezone: '',
      coordinates: '',
      postalCode: '',
      state: '',
      city: '',
      latitude: undefined,
      longitude: undefined,
      radius: 50,
      siret: undefined,
    },
    validate: (data) => {
      const errors: FormErrors<WorkplaceFormValues> = {}
      if (!data.name) {
        errors.name = i18n.t('common.empty')
      }

      if (!data.timezone) {
        errors.timezone = i18n.t('common.empty')
      }

      if (!data.country) {
        errors.country = i18n.t('common.empty')
      }

      if (hasGeoLocation) {
        if (!data.addressLineOne) {
          errors.addressLineOne = i18n.t('common.empty')
        }

        if (!data.postalCode) {
          errors.postalCode = i18n.t('common.empty')
        }
        if (!data.state) {
          errors.state = i18n.t('common.empty')
        }
        if (!data.city) {
          errors.city = i18n.t('common.empty')
        }
        if (!data.radius) {
          errors.radius = i18n.t('common.empty')
        }
      }
      return errors
    },
    onSubmit: async (data) => {
      const values = {
        ...data,
        radius: Number(data.radius),
        companyId: company.id,
      }

      const handle = await handleCreate(values)

      if (handle.success) {
        cache.evict('locations')

        navigate(navigation.settings.locations.fullPath())
      }

      return handle
    },
  })
  const { placesLibraryLoaded } = React.useContext(GoogleMapsContext)

  const { place, suggestions, onPlaceSelected } = useAutoCompleteAddress({
    inputRef,
    placesLibraryLoaded,
  })

  useEffect(() => {
    if (!place) return

    workplaceForm.setFieldValue('addressLineOne', place.address)
    workplaceForm.setFieldValue('city', place.city)
    workplaceForm.setFieldValue('postalCode', place.postcode)
    workplaceForm.setFieldValue('state', place.state)
    workplaceForm.setFieldValue('country', place.country)
    workplaceForm.setFieldValue('timezone', place.timeZone)
    workplaceForm.setFieldValue('latitude', place.lat)
    workplaceForm.setFieldValue('longitude', place.lng)
  }, [place?.address])

  const onMarkerChange = (lat: number | undefined, lng: number | undefined) => {
    workplaceForm.setFieldValue('latitude', lat)
    workplaceForm.setFieldValue('longitude', lng)
  }

  const showMapSidePanel = () => {
    setShowMapPanel(true)
  }
  const hideMapSidePanel = () => {
    setShowMapPanel(false)
  }

  const editLocationButton = (
    <EditLocationButton
      lat={workplaceForm.values.latitude || 0}
      lng={workplaceForm.values.longitude || 0}
      onClick={showMapSidePanel}
    />
  )

  const frenchCompany = workplaceForm.values.country == 'fr'

  return (
    <Section
      illustration="location-building-pin"
      title={i18n.t('settings.locations.add.section_title')}
      description={i18n.t('settings.locations.add.section_description')}
    >
      <FormRow padding="s0">
        <Box background="grey200" borderRadius={{ all: 'abs008' }} padding="s32" gap="s12">
          <TextField
            label={i18n.t('settings.locations.add.form.name')}
            value={workplaceForm.values.name}
            onChange={(value) => workplaceForm.setFieldValue('name', value)}
            mandatory
            error={workplaceForm.errors.name}
          />
          <TextField
            label={i18n.t('settings.locations.add.form.phone')}
            value={workplaceForm.values.phoneNumber}
            onChange={(value) => workplaceForm.setFieldValue('phoneNumber', value)}
          />
          {frenchCompany && (
            <TextField
              label={i18n.t('settings.locations.add.form.siret')}
              value={workplaceForm.values.siret}
              onChange={(value) => workplaceForm.setFieldValue('siret', value)}
            />
          )}
          {hasGeoLocation ? (
            <>
              <AutoCompleteSearchField
                label={i18n.t('settings.locations.add.form.address')}
                suggestions={suggestions}
                value={workplaceForm.values.addressLineOne}
                onSelect={({ value, label }: { value: string; label: string }) => {
                  workplaceForm.setFieldValue('addressLineOne', label)
                  onPlaceSelected(value)
                  setShowConfirmAddressPopover(true)
                }}
                getRef={(ref) => (ref ? (inputRef.current = ref) : null)}
                onChange={(value) => workplaceForm.setFieldValue('addressLineOne', value)}
                mandatory={hasGeoLocation}
                error={workplaceForm.errors.addressLineOne}
              />
              <Box>
                <Hint.Popover.Provider steps={1}>
                  <Hint.Popover
                    step={1}
                    title={i18n.t('settings.locations.add.form.popover.title')}
                    description={i18n.t('settings.locations.add.form.popover.description')}
                    actionLabel={i18n.t('common.close')}
                    position="left top"
                  ></Hint.Popover>
                </Hint.Popover.Provider>
              </Box>
            </>
          ) : (
            <TextField
              label={i18n.t('settings.locations.add.form.address')}
              value={workplaceForm.values.addressLineOne}
              onChange={(value) => workplaceForm.setFieldValue('addressLineOne', value)}
            />
          )}
          {(place || !hasGeoLocation) && (
            <>
              <TextField
                label={i18n.t('settings.locations.add.form.address_add_info')}
                value={workplaceForm.values.addressLineTwo}
                onChange={(value) => workplaceForm.setFieldValue('addressLineTwo', value)}
              />
              <Box flexDirection="row" gap="s12">
                <TextField
                  label={i18n.t('settings.locations.add.form.city')}
                  value={workplaceForm.values.city}
                  onChange={(value) => workplaceForm.setFieldValue('city', value)}
                  mandatory={hasGeoLocation}
                  autofilled={!!place?.city && place.city === workplaceForm.values.city}
                  error={workplaceForm.errors.city}
                />
                <TextField
                  label={i18n.t('settings.locations.add.form.zip_code')}
                  value={workplaceForm.values.postalCode}
                  onChange={(value) => workplaceForm.setFieldValue('postalCode', value)}
                  mandatory={hasGeoLocation}
                  autofilled={
                    !!place?.postcode && place.postcode === workplaceForm.values.postalCode
                  }
                  error={workplaceForm.errors.postalCode}
                />
              </Box>
              <TextField
                label={i18n.t('settings.locations.add.form.region')}
                value={workplaceForm.values.state}
                onChange={(value) => workplaceForm.setFieldValue('state', value)}
                mandatory={hasGeoLocation}
                autofilled={!!place?.state && place.state === workplaceForm.values.state}
                error={workplaceForm.errors.state}
              />
              <Select
                none={i18n.t('common.select')}
                onChange={(value) => workplaceForm.setFieldValue('country', value)}
                label={i18n.t('settings.locations.add.form.country')}
                options={countryList}
                value={workplaceForm.values.country}
                mandatory
                autofilled={!!place?.country && place.country === workplaceForm.values.country}
                error={workplaceForm.errors.country}
              />
              <Select
                none={i18n.t('common.select')}
                label={i18n.t('settings.locations.add.form.time_zone')}
                onChange={(value) => workplaceForm.setFieldValue('timezone', value)}
                options={timezoneOptions()}
                value={workplaceForm.values.timezone}
                mandatory
                autofilled={!!place?.timeZone && place.timeZone === workplaceForm.values.timezone}
                error={workplaceForm.errors.timezone}
              />
              {hasGeoLocation && (
                <Select
                  onChange={(value) => workplaceForm.setFieldValue('radius', value)}
                  none={i18n.t('common.select')}
                  label={i18n.t('settings.locations.map.radius_label')}
                  options={[
                    {
                      value: 50,
                      label: i18n.t('settings.locations.map.radius_options.within_less_one_block'),
                    },
                    {
                      value: 100,
                      label: i18n.t('settings.locations.map.radius_options.within_one_block'),
                    },
                    {
                      value: 500,
                      label: i18n.t('settings.locations.map.radius_options.within_few_blocks'),
                    },
                    {
                      value: 1000,
                      label: i18n.t('settings.locations.map.radius_options.within_huge_perimeter'),
                    },
                  ]}
                  value={workplaceForm.values.radius.toString()}
                  error={workplaceForm.errors.radius}
                  mandatory={hasGeoLocation}
                />
              )}
            </>
          )}
          {hasGeoLocation && workplaceForm.values.latitude && workplaceForm.values.longitude && (
            <StaticMap
              lat={workplaceForm.values.latitude}
              lng={workplaceForm.values.longitude}
              editLocationButton={editLocationButton}
              radius={workplaceForm.values.radius}
              showPopover={showConfirmAddressPopover}
              onPopupClose={() => setShowConfirmAddressPopover(false)}
            />
          )}
          <Box alignItems="flexEnd">
            <Button onClick={() => workplaceForm.handleSubmit()}>
              {i18n.t('settings.locations.add.form.submit')}
            </Button>
          </Box>
        </Box>
      </FormRow>
      <MapSidePanel
        show={showMapPanel}
        onClose={hideMapSidePanel}
        lat={workplaceForm.values.latitude || 0}
        lng={workplaceForm.values.longitude || 0}
        radius={workplaceForm.values.radius || 0}
        onMarkerChange={onMarkerChange}
      />
    </Section>
  )
}

export default observer(LocationWorkplacesForm)
